import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

// Shared promise for deduplicating concurrent fetchUserProfile calls
let profileFetchPromise = null;

/**
 * Statuses that mean "the admin/server has revoked access" — not "still
 * onboarding". When the current user's status flips to one of these mid-session,
 * the next profile fetch detects it and force-logs them out.
 */
const DEACTIVATED_STATUSES = new Set(['INACTIVE', 'SUSPENDED', 'ARCHIVED']);

const DEACTIVATION_MESSAGES = {
  INACTIVE: 'Your account has been deactivated by an administrator.',
  SUSPENDED: 'Your account has been suspended. Contact support.',
  ARCHIVED: 'Your account has been archived. Contact support to reactivate.',
};

// Guard against firing the deactivation logout sequence twice (can race if a
// component mid-render also triggers fetchUserProfile during the redirect).
let forceLogoutInFlight = false;

const forceLogoutForStatus = (status, logout, setStore) => {
  if (forceLogoutInFlight) return;
  forceLogoutInFlight = true;

  toast.warning(DEACTIVATION_MESSAGES[status] || 'Your account is no longer active.', {
    position: 'bottom-right',
    duration: 12000,
  });

  // 1) Clear local state immediately so ProtectedRoute sees signed-out on the
  //    very next render, even if the network cleanup below hangs.
  try { logout(); } catch { /* best-effort */ }
  try { setStore({ userProfile: null }); } catch { /* best-effort */ }

  // 2) Best-effort server cleanup — fire-and-forget so a hanging /users/logout
  //    or supabase signOut request can't block the redirect.
  (async () => {
    try {
      const { apiClient } = await import('@/lib/api');
      await apiClient.logoutUser();
    } catch { /* swallow */ }
    try { await supabase.auth.signOut(); } catch { /* swallow */ }
  })();

  // 3) Hold a few seconds so the toast is readable before the hard-redirect
  //    tears it down. The local state was cleared above, so route guards
  //    already prevent any sensitive screens from being interacted with.
  setTimeout(() => {
    window.location.href = '/';
  }, 5000);
};

export const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      userProfile: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,
      authType: "home",

      // Actions
      setUser: (user) => {
        console.log("🔧 AuthStore: Setting user", user);
        console.log("🔧 AuthStore: Before set - current state:", get());
        set({ user, isAuthenticated: !!user });
        console.log("🔧 AuthStore: After set - new state:", get());
      },

      // Set user profile
      setUserProfile: (userProfile) => {
        console.log("🔧 AuthStore: Setting user profile", userProfile);
        set({ userProfile });
      },

      // Fetch and set user profile from API (deduplicated — concurrent calls share one request)
      fetchUserProfile: async () => {
        if (profileFetchPromise) {
          return profileFetchPromise;
        }

        // Only show loading spinner if not yet initialized (first load)
        const wasInitialized = get().isInitialized;
        if (!wasInitialized) {
          set({ isLoading: true });
        }

        profileFetchPromise = (async () => {
          try {
            const { apiClient } = await import('@/lib/api');
            const profile = await apiClient.getUserProfile();
            console.log("✅ AuthStore: User profile fetched:", profile);

            // Mid-session deactivation check. If we already had a userProfile
            // (i.e., this isn't the initial sign-in fetch — SignIn/AuthCallback
            // handle that path themselves) and the server now reports a
            // deactivated status, force the user out with a clear toast.
            const newStatus = (profile?.status || '').toUpperCase();
            const previousProfile = get().userProfile;
            if (previousProfile && DEACTIVATED_STATUSES.has(newStatus)) {
              set({ isLoading: false });
              forceLogoutForStatus(newStatus, get().logout, set);
              return null;
            }

            set({ userProfile: profile, isLoading: false });
            return profile;
          } catch (error) {
            console.error("❌ AuthStore: Error fetching user profile:", error);
            // Clear the cached profile on any fetch failure so a
            // stale profile from a previous session (persisted via
            // partialize) doesn't shadow the truth on the next
            // consumer read. The resume helper's callers can then
            // fall back to getUserByUserId cleanly.
            set({ userProfile: null, isLoading: false });
            return null;
          } finally {
            profileFetchPromise = null;
          }
        })();

        return profileFetchPromise;
      },

      // Sync with Supabase session
      syncWithSupabase: async () => {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          console.log("🔄 AuthStore: Syncing with Supabase session", session);

          if (session?.user) {
            set({
              user: session.user,
              isAuthenticated: true
              // Don't set isLoading: false here - let fetchUserProfile handle it
            });
            // Fetch user profile after successful auth
            await get().fetchUserProfile();
          } else {
            set({
              user: null,
              userProfile: null,
              isAuthenticated: false,
              isLoading: false
            });
          }
        } catch (error) {
          console.error("❌ AuthStore: Error syncing with Supabase", error);
          set({ isLoading: false });
        }
      },
      
      // Initialize store with current session
      initialize: async () => {
        // Only show loading spinner on first initialization
        if (!get().isInitialized) {
          set({ isLoading: true });
        }
        await get().syncWithSupabase();
        set({ isInitialized: true });

        // Set up auth state change listener
        supabase.auth.onAuthStateChange(async (event, session) => {
          console.log("🔄 AuthStore: Auth state changed", event, session);

          if (session?.user) {
            // Set loading true for SIGNED_IN to prevent redirect before profile is fetched
            const shouldFetchProfile = event === 'SIGNED_IN';
            set({
              user: session.user,
              isAuthenticated: true,
              isLoading: shouldFetchProfile
            });
            // Fetch user profile after OAuth sign-in
            if (shouldFetchProfile) {
              await get().fetchUserProfile();
            }
          } else {
            set({
              user: null,
              userProfile: null,
              isAuthenticated: false
              // Don't automatically set authType to "home" - let LayoutProvider handle it
            });
          }
        });
      },
      setLoading: (isLoading) => {
        console.log("🔧 AuthStore: Setting loading", isLoading);
        set({ isLoading });
      },
      logout: () => {
        console.log("🔧 AuthStore: Logging out");
        // Clear booking confirmation keys from localStorage
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith('booking_confirmed_') || key.startsWith('flights_created_')) localStorage.removeItem(key);
        });
        // Wipe the cached userProfile too. Otherwise the persisted
        // profile (from `partialize`) survives across the logout, and
        // the next sign-in reads the STALE profile before
        // fetchUserProfile has a chance to refresh — the resume helper
        // then routes on the old status (e.g. still shows onboarding
        // even after the admin flipped status to ACTIVE).
        set({ user: null, userProfile: null, isAuthenticated: false, authType: "home" });
        // Kill the Supabase session too. Without this, `getSession()`
        // still returns the previous user on the next page load, so
        // navigating to any page that reads the session (invite
        // acceptance, onboarding, dashboard) picks the previous user
        // back up — a cross-account leak if the next visitor opens a
        // link meant for someone else. Fire-and-forget so a hanging
        // network call can't block the UI.
        supabase.auth.signOut().catch((err) => {
          console.warn('AuthStore: supabase.signOut() failed', err);
        });
      },
      setAuthType: (authType) => {
        console.log("🔧 AuthStore: Setting authType", authType);
        set({ authType });
      },
      
      // Getters
      getUser: () => get().user,
      isLoggedIn: () => get().isAuthenticated,
      getAuthType: () => get().authType,
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, userProfile: state.userProfile, isAuthenticated: state.isAuthenticated, authType: state.authType }),
      onRehydrateStorage: () => (state) => {
        console.log("🔄 AuthStore: Rehydrating from localStorage", state);
      },
    }
  )
);
